import React, { useEffect } from "react";

/**
 * SEO component — sets <title>, <meta>, OpenGraph, and JSON-LD structured data.
 * No external deps; manipulates document head directly.
 */
export default function SEO({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} · Tharlo` : "Tharlo · Better Properties. Better Decisions.";
    document.title = fullTitle;

    const setMeta = (sel, attr, key, value) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    // robots meta — flips to noindex,nofollow on private/authed pages (dashboards, admin).
    // Public pages always get index,follow (default in index.html — we set it explicitly here to
    // recover from any prior noindex applied by an earlier SPA route.)
    setMeta('meta[name="robots"]', "name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    const desc = description || "Tharlo is a premium real estate marketplace in Rewa, Madhya Pradesh — verified properties, legal transparency, and curated discovery for serious buyers, investors, and NRIs.";
    setMeta('meta[name="description"]', "name", "description", desc);
    setMeta('meta[property="og:title"]', "property", "og:title", fullTitle);
    setMeta('meta[property="og:description"]', "property", "og:description", desc);
    setMeta('meta[property="og:type"]', "property", "og:type", type);
    setMeta('meta[property="og:site_name"]', "property", "og:site_name", "Tharlo");
    if (image) setMeta('meta[property="og:image"]', "property", "og:image", image);
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", fullTitle);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", desc);
    if (image) setMeta('meta[name="twitter:image"]', "name", "twitter:image", image);

    const url = canonical || window.location.href;
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", url);
    setMeta('meta[property="og:url"]', "property", "og:url", url);

    // JSON-LD — only manage if this instance provides jsonLd
    if (jsonLd) {
      const sigAttr = `data-tharlo-jsonld-${(canonical || title || "page").replace(/[^a-z0-9]/gi, "").slice(0,20)}`;
      document.querySelectorAll(`script[${sigAttr}]`).forEach((s) => s.remove());
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(sigAttr, "1");
      s.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [title, description, canonical, image, type, jsonLd, noindex]);

  return null;
}

export const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  name: "Tharlo",
  url: "https://tharlo.in",
  logo: "https://tharlo.in/logo.png",
  description: "Verified premium real estate marketplace for Rewa, Madhya Pradesh.",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Rewa",
    addressRegion: "Madhya Pradesh",
    addressCountry: "IN",
  },
  areaServed: { "@type": "City", name: "Rewa" },
  slogan: "Better Properties. Better Decisions.",
};

export const propertyJsonLd = (p) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: p.title,
  description: p.description,
  image: p.images?.[0],
  brand: { "@type": "Brand", name: "Tharlo" },
  offers: {
    "@type": "Offer",
    price: p.price,
    priceCurrency: "INR",
    availability: "https://schema.org/InStock",
    url: typeof window !== "undefined" ? window.location.href : `https://tharlo.in/property/${p.slug}`,
  },
  additionalProperty: [
    { "@type": "PropertyValue", name: "Property Type", value: p.property_type },
    { "@type": "PropertyValue", name: "Area", value: `${p.area_value} ${p.area_unit}` },
    { "@type": "PropertyValue", name: "Locality", value: `${p.locality}, ${p.city}` },
    p.legal_status ? { "@type": "PropertyValue", name: "Legal Status", value: p.legal_status } : null,
  ].filter(Boolean),
});
